<template>
    <div>
        <h1 class="display-heading">Bulk Add Employee</h1>
        <template v-if="!isPreview">
            <div class="mb-3">
                <h5>Instructions</h5>
                <ol>
                    <li>
                        Download the sample file template here.
                        <div><a href="/static/samples/sample_bulk_add_employee_self_paying.xlsx" target="_blank" class="mt-1 mb-2 btn btn-primary btn-primary-insv">Download Template File</a></div>
                    </li>
                    <li>
                        Open the downloaded file and fill up the file with the required fields. Example as below.
                        <div class="mt-1 mb-2">
                            <img src="/static/samples/sample_bulk_add_employee_self_paying.png" class="img img-fluid">
                        </div>
                    </li>

                    <li>Select and upload the file here.</li>
                    <li>Review the user list and proceed!</li>
                </ol>
            </div>

            <div class="card">
                    <div class="card-header">Upload User List</div>
                    <div class="card-body">
                        <form @submit.prevent="validateForm">
                            <div>
                                <div class="form-group row">
                                    <label class="col-sm-3 col-xl-2 col-form-label">User List File</label>
                                    <div class="col-sm-9 col-xl-10">
                                        <input id="uploadFileInput" type="file" v-validate="'required|mimes:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'" data-vv-as="user list file" name="userList"
                                            :class="{'is-invalid':  validationErrors.has('userList')}">
                                        <div v-show="validationErrors.has('userList')" class="invalid-feedback">
                                            {{ validationErrors.first('userList') }}
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-sm-9 offset-sm-3 col-xl-10 offset-xl-2">
                                        <p v-show="isSubmitMessageShown" :class="{
                                            'text-danger': isSubmitMessageError,
                                            'mb-3': true
                                        }">
                                            {{submitMessage}}
                                        </p>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-sm-9 offset-sm-3 col-xl-10 offset-xl-2">
                                        <button class="btn btn-primary btn-primary-insv">Upload User List File</button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
        </template>
        <template v-else>
            <h5>Preview Content</h5>
            <div class="mb-3">
                <p>Kindly review your uploaded invitee list: (triple check that all entries are correct!)</p>
                <div ref="tableSection" style="max-height: 400px; overflow-y: auto;">
                    <h6>Employees</h6>
                    <div class="table-responsive">
                        <table ref="listTable" class="table table-hover">
                            <colgroup>
                                <col width="40">
                                <col width="150">
                                <col width="100">
                                <col width="100">
                                <col width="100">
                                <col>
                                <col>
                                <col>
                                <col>
                                <col>
                                <col>
                                <col>
                            </colgroup>
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col" class="text-nowrap">Full Name</th>
                                    <th scope="col" class="text-nowrap">Identity Type</th>
                                    <th scope="col" class="text-nowrap">Identity Number</th>
                                    <th scope="col" class="text-nowrap">Birth Date</th>
                                    <th scope="col" class="text-nowrap">Gender</th>
                                    <th scope="col" class="text-nowrap">Email Address</th>
                                    <th scope="col" class="text-nowrap">Phone Number</th>
                                    <th scope="col" class="text-nowrap">Employee Number</th>
                                    <th scope="col" class="text-nowrap">Department</th>
                                    <th scope="col" class="text-nowrap">Join Date</th>
                                    <th scope="col" class="text-nowrap">Deactivation Date</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="(employee, index) in employeeList" :key="index">
                                    <th scope="col">{{index+1}}</th>
                                    <td>
                                        {{employee.fn ? employee.fn : '-'}}
                                    </td>
                                    <td>
                                        {{identificationTypes.find(t => t.value == employee.it).text}}
                                    </td>
                                    <td>
                                        {{employee.in ? employee.in : '-'}}
                                    </td>
                                    <td>
                                        {{employee.dob ? employee.dob : '-'}}
                                    </td>
                                    <td>
                                        <span v-if="employee.g===1">
                                            Male
                                        </span>
                                        <span v-else-if="employee.g===2">
                                            Female
                                        </span>
                                        <span v-else>
                                            Unspecified
                                        </span>
                                    </td>
                                    <td>
                                        {{employee.e ? employee.e : '-'}}
                                    </td>
                                    <td>
                                        {{employee.pn ? employee.pn : '-'}}
                                    </td>
                                    <td>
                                        {{employee.en ? employee.en : '-'}}
                                    </td>
                                    <td>
                                        {{employee.d ? employee.d : '-'}}
                                    </td>
                                    <td>
                                        {{employee.jd}}
                                    </td>
                                    <td>
                                        {{employee.dd}}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <p>*{{employeeList.length}} row{{employeeList.length>1 ? 's' : ''}} of employee detected.</p>
            </div>
            <button type="button" class="btn btn-primary btn-primary-insv mr-2" @click="confirmInvitation">Confirm and Send Invitations</button>
            <button type="button" class="btn btn-secondary" @click="startOver">Reset</button>
        </template>

        <popup-modal ref="popupAddDone" title="Bulk Add / Update Employee Details" :hasCloseButton="false" sizeClass="modal-lg">
            <template slot="content">
                <p v-html="addResultText"></p>

                <template v-if="addWithErrors">
                    <p>{{addResultErrorText}}</p>

                    <table v-if="addErrorList.length > 0" class="table">
                        <colgroup>
                            <col width="50">
                            <col>
                            <col>
                            <col>
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col">Name</th>
                                <th scope="col">Identity Number</th>
                                <th scope="col">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(invite, index) in addErrorList" :key="index">
                                <th scope="row">{{index+1}}</th>
                                <td>{{invite.name ? invite.name : '-'}}</td>
                                <td>{{invite.nric}}</td>
                                <td>{{invite.reason ? invite.reason : '-'}}</td>
                            </tr>
                        </tbody>
                    </table>
                </template>
            </template>
            <template slot="footer">
                <template v-if="!addWithErrors">
                    <button type="button" class="btn btn-primary btn-primary-insv mr-2" @click="viewEmployeeTier">View Employees</button>
                    <button type="button" class="btn btn-primary btn-primary-insv" @click="dismissDoneConfirmation">Upload Another List</button>
                </template>
                <template v-else>
                    <button type="button" class="btn btn-primary btn-primary-insv mr-2" @click="tryAgain">Try Again</button>
                    <button type="button" class="btn btn-primary mr-2" @click="viewEmployeeTier">Discard Errors</button>
                    <button type="button" class="btn btn-secondary" @click="cancelTry">Cancel</button>
                </template>
            </template>
        </popup-modal>
    </div>
</template>

<script>
import {
    convertDateToDDMMYYYY,
    convertDateToIso8601
} from '@/utilities';
import identificationTypes from '@/config/identificationTypes';
import popupModal from '@/components/popup-modal';
import {
    Validator
} from 'vee-validate';
export default {
    'name': 'BulkUploadEmployeeSelfPaying',
    'components': {
        popupModal
    },
    data () {
        return {
            'isSubmitMessageShown': false,
            'isSubmitMessageError': false,
            'submitMessage': '',
            'isPreview': false,
            'rowMessage': '',
            'employeeList': [],
            'dependentList': [],

            'addWithErrors': false,
            'addResultText': '',
            'addResultErrorText': '',
            'addErrorList': [],
            'addSuccessList': [],
            identificationTypes
        };
    },
    mounted () {
        const dict = {
            'custom': {
                'drugList': {
                    'required': 'You must select a file to upload.',
                    'mimes': 'The selected file must be an excel file.'
                }
            }
        };
        Validator.localize('en', dict);
        this.$validator.localize('en', dict);
    },
    'methods': {
        validateForm () {
            this.isSubmitMessageShown = true;
            this.isSubmitMessageError = false;
            this.$validator.validateAll().then((isValidate) => {
                if (isValidate) {
                    let bulkFile = document.querySelector('#uploadFileInput');
                    let formData = new FormData();
                    formData.append('BulkInviteFile', bulkFile.files[0]);
                    this.submitMessage = 'Uploading and processing bulk invite file.';

                    this.axios.post('employeeTier/BulkVerifySelfPayingEmployeeFile', formData, {
                        'headers': {
                            'Content-Type': 'multipart/form-data'
                        } }).then(({ data }) => {
                        if (data.data.s) {
                            this.isSubmitMessageError = false;

                            this.employeeList = data.data.el.map((employee) => ({
                                'fn': employee.fn,
                                'it': employee.it,
                                'in': employee.in,
                                'dob': employee.dob ? convertDateToDDMMYYYY(employee.dob) : null,
                                'g': employee.g,
                                'e': employee.e,
                                'pn': employee.pn,
                                'en': employee.en,
                                'd': employee.d,
                                'tn': employee.tn,
                                'jd': employee.jd ? convertDateToDDMMYYYY(employee.jd) : null,
                                'dd': employee.dd ? convertDateToDDMMYYYY(employee.dd) : null
                            }));
                            this.dependentList = data.data.dl.map((dependent) => ({
                                'fn': dependent.fn,
                                'it': dependent.it,
                                'in': dependent.in,
                                'dob': dependent.dob ? convertDateToDDMMYYYY(dependent.dob) : null,
                                'e': dependent.e,
                                'r': dependent.r,
                                'pi': dependent.pi
                            }));

                            this.isPreview = true;
                        } else {
                            this.isSubmitMessageError = true;
                            this.submitMessage = data.data.e;
                        }
                    }).catch((error) => {
                        this.isSubmitMessageError = true;
                        if (error.response.data) {
                            this.submitMessage = error.response.data.message;
                        } else {
                            this.submitMessage = 'Error processing bulk upload. Please try again.';
                        }
                    });
                } else {
                    this.isSubmitMessageError = true;
                    this.submitMessage = 'You have error in the form. Please check your input(s) before saving your invitation request.';
                }
            });
        },
        confirmInvitation () {
            let tempData = {
                'el': JSON.parse(JSON.stringify(this.employeeList)),
                'dl': JSON.parse(JSON.stringify(this.dependentList))
            };
            tempData.el.forEach(employee => {
                employee.dob = convertDateToIso8601(employee.dob);
                employee.jd = convertDateToIso8601(employee.jd);
                employee.dd = convertDateToIso8601(employee.dd);
            });
            tempData.dl.forEach(dependent => {
                dependent.dob = convertDateToIso8601(dependent.dob);
            });
            this.axios.post('employeeTier/ConfirmBulkUploadSelfPayingEmployeeList', tempData).then(({
                data
            }) => {
                let successList = data.data.sl;
                this.addSuccessList = successList;
                let failedList = data.data.fl;
                let successEmployeeCount = successList.filter(function (element) { return element.ie; }).length;

                this.addResultText =
                                `${successEmployeeCount} employee${successEmployeeCount > 1 ? 's' : ''} assigned / updated.`;
                const createResult = data.data.cur;
                if (createResult) {
                    // existingEmployeeCount: 0
                    // hasInviteNewDependentOrSpouse: false
                    // hasInviteNewEmployee: false
                    // newEmployeeCount: 0
                    // newEmployeeDependentCount: 0
                    // newEmployeeSpouseCount: 0
                    // updatedEmployeeDependentCount: 0
                    this.addResultText += `<br>Detected ${createResult.newEmployeeCount} new employee(s).`;

                    if (createResult.newEmployeeCount > 0) {
                        this.addResultText += '<br>Your new employee(s) will receive email(s) from us to create new user ID(s) and password(s).';
                    }
                }

                if (failedList.length > 0) {
                    this.addWithErrors = true;
                    this.addResultErrorText = `${failedList.length} failed creation${failedList.length > 1 ? 's' : ''} of employee.`;
                    this.addErrorList = failedList.map((invitation) => ({
                        'name': invitation.fn,
                        'nric': invitation.n,
                        'reason': invitation.fr
                    }));
                } else {
                    this.addWithErrors = false;
                    this.addResultErrorText = '';
                    this.addErrorList = [];
                }

                this.$refs.popupAddDone.show();
            }).catch(() => {
                this.addWithErrors = true;
                this.addResultErrorText = 'Error processing employee list. Please try again.';
                this.$refs.popupAddDone.show();
            });
        },
        startOver () {
            window.location.reload();
        },
        dismissDoneConfirmation () {
            this.$refs.popupAddDone.hide().then(() => {
                window.location.reload();
            });
        },
        viewEmployeeTier () {
            this.$refs.popupAddDone.hide().then(() => {
                this.$router.push({
                    'name': 'employee-self-paying-list'
                });
            });
        },
        tryAgain () {
            this.$refs.popupAddDone.hide().then(() => {
                this.confirmInvitation();
            });
        },
        cancelTry () {
            this.$refs.popupAddDone.hide();
        }
    }
};

</script>

<style>

</style>
